import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import { task } from 'ember-concurrency';
import type Ota from 'frontend-utils/models/ota';
import {
  OTA_IDS,
  type OtaId,
} from 'frontend-utils/utils/constants/constant-values';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import ENV from 'lumen/config/environment';
import type {
  Carate,
  Carecommendation,
  ChannelPricingSubscriptionConfig,
  Period,
  RestrictionRecommendation,
} from 'lumen/models';
import type { FormattedOffset } from 'lumen/models/channel-pricing-subscription-config';
import type HotelChannelPricingRoute from 'lumen/routes/hotel/channel-pricing';
import type { LiveupdateBulkOptions } from 'lumen/services/channel-pricing/liveupdate';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  latestChannelExceptionRestrictionActionsPerDayAndChannel,
  latestHotelLevelRestrictionActionsPerDay,
  latestMinorChannelExceptionRestrictionActionsPerDayAndChannel,
} from 'lumen/utils/channel-pricing/restriction-actions-utils';
import { UNSUPPORTED_CLOSING_THE_LOOP_CHANNELS } from 'lumen/utils/constants/channel-pricing-constants';
import type { ModelFrom } from 'lumen/utils/routes';

type ChannelTabData = {
  iconName: IconBrandName | '';
  id: string;
  label: string;
  numberOfRecommendations?: number;
  order: number;
};

export default class HotelChannelPricingController extends Controller {
  declare model: ModelFrom<HotelChannelPricingRoute>;
  queryParams = ['fromDate'];

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service('channel-pricing/liveupdate')
  declare liveupdate: Services['channel-pricing/liveupdate'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare roiTrophies: Services['roi-trophies'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeChannelTabId: OtaId | null = null;
  @tracked fromDate = '';
  @tracked isCompareChannelPricesModalOpen = false;

  CHANNEL_TAB_OTHERS_ID = 'OTHER';

  startLiveupdateTask = task(
    async (
      channelsToLiveupdate: OtaId[],
      liveupdateOptions: LiveupdateBulkOptions,
    ): Promise<void> => {
      const result = await this.liveupdate.startLiveupdateTask.perform(
        channelsToLiveupdate,
        liveupdateOptions,
      );

      if (result) {
        this.toast.success(
          this.intl.t('channel_pricing.toast.liveshop_started', {
            channels: this.formattedEligibleChannelNamesForClosingTheLoop,
          }),
          {
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          },
        );

        this.usageAnalytics.track('Live Shop Started', {
          eventTrigger: 'smart-distribution',
          product: 'smart-distribution',
        });
      }
    },
  );

  get activeChannelTabIdWithInitialValue(): OtaId {
    if (this.activeChannelTabId !== null) {
      return this.activeChannelTabId;
    }

    // Optional: this getter can run before the rate preference lands. It falls
    // through to the fallback below and recomputes once the value arrives.
    const preferredChannelId =
      this.hotelAndMarketService.hotel.paRatePreference?.channel;
    const availableTabs = this.visibleChannelTabs.tabData;

    // Use preferred channel if it's available
    if (availableTabs.some((tab) => tab.id === preferredChannelId)) {
      return preferredChannelId as OtaId;
    }

    // Check if Booking.com is available in the tabs
    const hasBookingDotCom = availableTabs.some(
      (tab) => tab.id === OTA_IDS.BOOKINGDOTCOM,
    );

    // If Booking.com is not available, use the first available tab
    // (if no channels are active, this will be the "other" channel)
    if (!hasBookingDotCom) {
      return availableTabs[0]?.id as OtaId;
    }

    // Default to Booking.com
    return OTA_IDS.BOOKINGDOTCOM;
  }

  get areChannelsTableDataRequestsLoading(): boolean {
    return (
      this.model.requestRestrictionActionsOnPageLoadTask.isRunning ||
      this.model.ratesAllChannelsRequestTask.isRunning ||
      this.model.restrictionsRecommendationsRequestTask.isRunning ||
      this.model.requestChannelPricingSubscriptionConfigTask.isRunning ||
      this.model.ratePreferenceTaskResult.isRunning
    );
  }

  get arePageRequestsLoading(): boolean {
    return (
      this.arePmsTableDataRequestsLoading ||
      this.areChannelsTableDataRequestsLoading ||
      this.model.occupancyDataTask.isRunning
    );
  }

  get arePmsTableDataRequestsLoading(): boolean {
    return (
      this.model.requestRestrictionActionsOnPageLoadTask.isRunning ||
      this.model.recommendationsRequestTask.isRunning ||
      this.model.pricingPmsRoomsAllRequestTask.isRunning ||
      this.model.restrictionsRequestTask.isRunning ||
      this.model.restrictionsRecommendationsRequestTask.isRunning
    );
  }

  get channelOffsetsByChannel(): Record<string, FormattedOffset> {
    if (!this.channelPricingConfig) {
      return {};
    }

    return this.channelPricingConfig.formattedOffsets;
  }

  get channelPricingConfig(): ChannelPricingSubscriptionConfig | null {
    if (this.model.requestChannelPricingSubscriptionConfigTask.isRunning) {
      return null;
    }

    return (
      this.model.requestChannelPricingSubscriptionConfigTask?.last?.value ??
      null
    );
  }

  /**
   * CLOSING THE LOOP TODO: check if we can also use this discount detection logic for notification badges
   * Loops through all carates to categorize major channels by their pricing status
   * Returns channels with detected discounts, channels with matching/higher shopped rates,
   * and the latest extract datetime across all carates
   */
  get channelsDiscountData(): {
    channelsWithDiscount: OtaId[];
    channelsWithMatchingOrHigherPrice: OtaId[];
    latestExtractDatetime: Dayjs | null;
  } {
    const discountOtas: OtaId[] = [];
    const allOtasWithPricing: OtaId[] = [];
    let latestExtractDatetime: Dayjs | null = null;

    const eligibleChannelIds = this.eligibleChannelsForClosingTheLoop.map(
      (channel) => channel.id as OtaId,
    );

    // Single loop through base room carates in periods to find discounts
    // Note: filter out days in the past because these are irrelevant for discounts.
    // These can be present if the user is watching the current week
    this.periods
      .filter((period) => {
        return !period.day.isPast;
      })
      .forEach((period) => {
        period.ownHotelCarates.forEach((carate) => {
          const carateOta = carate.ota as OtaId;

          if (
            carate.baseRoom &&
            carateOta &&
            eligibleChannelIds.includes(carateOta)
          ) {
            // Track the most recent extract datetime across eligible carates
            if (
              carate.extractDatetime &&
              (!latestExtractDatetime ||
                carate.extractDatetime.isAfter(latestExtractDatetime))
            ) {
              latestExtractDatetime = carate.extractDatetime;
            }

            // Add to all OTAs if not already included
            if (!allOtasWithPricing.includes(carateOta)) {
              allOtasWithPricing.push(carateOta);
            }

            // Add to discount OTAs if discount detected and not already included
            // Exclude channels where price action is newer than extract datetime (stale data)
            const priceActionCreatedAt = period.priceAction?.createdAt;
            const isRateDataStale =
              priceActionCreatedAt &&
              carate.extractDatetime &&
              carate.extractDatetime.isBefore(priceActionCreatedAt);

            if (
              carate.isRateDiscountDetected &&
              !isRateDataStale &&
              !discountOtas.includes(carateOta)
            ) {
              discountOtas.push(carateOta);
            }
          }
        });
      });

    // Everything else = matching or higher
    const matchingOrHigherOtas = allOtasWithPricing.filter(
      (ota) => !discountOtas.includes(ota),
    );

    return {
      channelsWithDiscount: discountOtas,
      channelsWithMatchingOrHigherPrice: matchingOrHigherOtas,
      latestExtractDatetime,
    };
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.fromDateMoment.startOf('week').startOf('day'),
      end: this.fromDateMoment.endOf('week').startOf('day'),
    };
  }

  get daysWithActivePricePush(): string[] {
    return this.ownHotelRecommendationsBeingActioned.map((carecommendation) => {
      return carecommendation.fromDate.format('YYYY-MM-DD');
    });
  }

  get daysWithFailedChannelLevelRestrictionPush(): Record<string, string[]> {
    return latestChannelExceptionRestrictionActionsPerDayAndChannel(
      this.periods,
    );
  }

  get daysWithFailedHotelLevelRestrictionPush(): string[] {
    return latestHotelLevelRestrictionActionsPerDay(this.periods);
  }

  get daysWithFailedMinorChannelLevelRestrictionPush(): Record<
    string,
    string[]
  > {
    return latestMinorChannelExceptionRestrictionActionsPerDayAndChannel(
      this.periods,
    );
  }

  get daysWithFailedPricePush(): string[] {
    return this.ownHotelRecommendations
      .filter((carecommendation) => {
        return carecommendation.hasException;
      })
      .map((carecommendation) => {
        return carecommendation.fromDate.format('YYYY-MM-DD');
      });
  }

  /**
   * Returns eligible channels for Closing The Loop features
   * Filters out unsupported channels (HRS and Brand.com)
   * @returns Array of Ota records for supported channels
   */
  get eligibleChannelsForClosingTheLoop(): Ota[] {
    if (!this.channelPricingConfig?.channels?.length) {
      return [];
    }

    return this.channelPricingConfig.channels.filter(
      (channel: Ota) =>
        !UNSUPPORTED_CLOSING_THE_LOOP_CHANNELS.includes(channel.id as OtaId),
    );
  }

  /**
   * Returns a formatted string of eligible channel names for Closing The Loop features
   * If no eligible channels, returns a 'missing' translation string
   * @returns Comma-separated string of eligible channel names or 'missing' text
   */
  get formattedEligibleChannelNamesForClosingTheLoop(): string {
    if (this.eligibleChannelsForClosingTheLoop.length === 0) {
      return this.intl.t('general.missing');
    }

    return this.eligibleChannelsForClosingTheLoop
      .sort((a, b) => a.order - b.order)
      .map((channel) => channel.name)
      .join(', ');
  }

  get fromDateMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(`${this.fromDate}`);
  }

  get hasChannelManager(): boolean {
    return this.access.hasChannelManagerTab;
  }

  get hasPricingAndDistribution(): boolean {
    return (
      this.access.hasFeature('app.channel_manager') &&
      this.access.hasFeature('app.pricing_assistant')
    );
  }

  get isCompareChannelPricesButtonDisabled(): boolean {
    return (
      this.liveupdate.areTasksRunning ||
      this.areChannelsTableDataRequestsLoading
    );
  }

  get isDayBarDataLoading(): boolean {
    return this.model.occupancyDataTask.isRunning;
  }

  get liveupdateButtonTooltipText(): string {
    if (this.eligibleChannelsForClosingTheLoop.length === 0) {
      return this.intl.t('general.missing');
    }

    return this.intl.t('channel_pricing.liveshop_button.tooltip', {
      channels: this.formattedEligibleChannelNamesForClosingTheLoop,
    });
  }

  get maxDateMoment(): Dayjs {
    return this.minDateMoment.add(12, 'month').endOf('week');
  }

  get minDateMoment(): Dayjs {
    return this.hotelAndMarketService
      .getLocalTime()
      .startOf('week')
      .startOf('day');
  }

  get minorChannels(): string[] {
    return this.channelPricingConfig?.minorChannels ?? [];
  }

  get ownHotelBaseroomCarates(): Carate[] {
    return this.ownHotelCarates.filter((carate) => {
      return (
        this.hotelAndMarketService.hotel.pricingIntegration
          .pms_leading_room_pms_id === carate.pmsRoomtype
      );
    });
  }

  get ownHotelCarates(): Carate[] {
    if (
      !this.model.pricingPmsRoomsAllRequestTask.isRunning &&
      this.model.pricingPmsRoomsAllRequestTask.last?.value
    ) {
      return this.model.pricingPmsRoomsAllRequestTask.last?.value;
    }

    return [];
  }

  get ownHotelRecommendations(): Carecommendation[] {
    const recommendations: Carecommendation[] = [];

    this.ownHotelCarates.forEach((carate) => {
      if (carate.carecommendation) {
        recommendations.push(carate.carecommendation);
      }
    });

    return recommendations;
  }

  get ownHotelRecommendationsBeingActioned(): Carecommendation[] {
    return this.ownHotelRecommendations.filter((carecommendation) => {
      return carecommendation.isBeingPushedOrIsPushed;
    });
  }

  get periods(): Period[] {
    if (
      !this.model.restrictionsRequestTask.isRunning &&
      this.model.restrictionsRequestTask.last?.value &&
      this.model.restrictionsRequestTask.last?.value.length
    ) {
      return this.model.restrictionsRequestTask.last?.value;
    }

    if (
      !this.model.ratesAllChannelsRequestTask.isRunning &&
      this.model.ratesAllChannelsRequestTask.last?.value
    ) {
      return this.model.ratesAllChannelsRequestTask.last?.value;
    }

    return [];
  }

  get showCompareChannelPrices(): boolean {
    return this.access.hasSmartDistributionClosingTheLoopFeature;
  }

  get visibleChannelTabs(): { tabData: ChannelTabData[]; tabIds: string[] } {
    const groupedRecommendations: Record<string, RestrictionRecommendation[]> =
      {};

    /*
     * Looping over restriction recommendations via the periods. Make sure that badge is updated once a restriction is actioned.
     * See INDU-529 bug ticket for more context
     */
    for (const period of this.periods) {
      period.restrictionRecommendations.forEach((restrictionRecommendation) => {
        const channelId = restrictionRecommendation.channelId;

        if (!channelId) {
          return;
        }

        if (!groupedRecommendations[channelId]) {
          groupedRecommendations[channelId] = [];
        }

        groupedRecommendations[channelId].push(restrictionRecommendation);
      });
    }

    const tabData: ChannelTabData[] = [];

    if (this.channelPricingConfig?.channels.length) {
      this.channelPricingConfig.channels.forEach((channel: Ota) => {
        if (!channel.id) {
          return;
        }

        tabData.push({
          id: channel.id,
          label:
            channel.id === OTA_IDS.BRANDDOTCOM
              ? this.intl.t('channel_pricing.channels_tab.own_website')
              : channel.name,
          iconName: channel.id as IconBrandName,
          order: channel.order,
          numberOfRecommendations: groupedRecommendations[channel.id]?.length,
        });
      });
    }

    tabData.sort((a, b) => a.order - b.order);

    if (this.channelPricingConfig?.minorChannels.length) {
      tabData.push({
        id: this.CHANNEL_TAB_OTHERS_ID,
        // SMART DISTRIBUTION TODO: Nitpick: the translation key isn't accurate anymore and could be updated later
        label: this.intl.t('channel_pricing.channels_dropdown.others'),
        iconName: '',
        order: 10000,
        numberOfRecommendations:
          groupedRecommendations[this.CHANNEL_TAB_OTHERS_ID]?.length,
      });
    }

    const tabIds = tabData.map((tab) => tab.id);

    return {
      tabIds,
      tabData,
    };
  }

  @action
  closeCompareChannelPricesModal(): void {
    this.isCompareChannelPricesModalOpen = false;
  }

  @action
  onSelectChannelTab(selectedChannelId: string): void {
    if (this.activeChannelTabId === selectedChannelId) {
      return;
    }

    this.activeChannelTabId =
      selectedChannelId as (typeof OTA_IDS)[keyof typeof OTA_IDS];

    this.usageAnalytics.track('Channel Changed', {
      eventTrigger: 'smart-distribution',
      product: 'smart-distribution',
      channelId: selectedChannelId,
    });
  }

  @action
  openChannelManager(): void {
    const { id } = this.hotelAndMarketService.hotel;
    openUrl(
      `${getHostDomain({ frontendGateway: ENV.frontendGateway })}/apigateway/v1/app/channel-management/channelmanager/outgoing?subscription_id=${id}`,
      true,
    );
  }

  @action
  openCompareChannelPricesModal(): void {
    if (this.isCompareChannelPricesButtonDisabled) {
      return;
    }

    this.isCompareChannelPricesModalOpen = true;

    this.usageAnalytics.track('Report Compare Channel Prices Viewed', {
      eventTrigger: 'smart-distribution',
      product: 'smart-distribution',
    });
  }

  @action
  startLiveupdate(): void {
    if (this.areChannelsTableDataRequestsLoading) {
      return;
    }

    const channels = this.channelPricingConfig?.channels ?? [];
    const channelsToLiveupdate = channels
      .filter(
        (channel) =>
          !UNSUPPORTED_CLOSING_THE_LOOP_CHANNELS.includes(channel.id as OtaId),
      )
      .map((channel) => channel.id as OtaId);

    const liveupdateConfig = {
      los: this.hotelAndMarketService.hotel.paRatePreference.los,
      mealtype: this.hotelAndMarketService.hotel.paRatePreference.mealtype,
      persons: this.hotelAndMarketService.hotel.paRatePreference.persons,
    };

    void this.startLiveupdateTask.perform(
      channelsToLiveupdate,
      liveupdateConfig,
    );
  }

  @action
  updateSelectedRange(selectedRange: { end: Dayjs; start: Dayjs }): void {
    this.usageAnalytics.track('Date Range Changed', {
      eventTrigger: 'date-picker',
      product: 'smart-distribution',
    });

    if (selectedRange) {
      this.fromDate = selectedRange.start.format('YYYY-MM-DD');
    }
  }
}
